import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrandBtn, CloseX, GhostBtn, Modal, Spinner, readTheme } from '../../components/studio/studioUi';
import { SEOHead } from '../../components/seo/SEOHead';
import { useToast } from '../../components/common/Toast';
import { errorText } from '../../services/video.service';
import { PROVIDER_LABEL, socialService, type ScheduledPost, type SocialAccount, type SocialConfig, type SocialProvider } from '../../services/social.service';
import { FONT_MONO, FONT_SANS, FONT_SERIF } from '../landing/landingData';
import { BetaChip, fieldLabel, input } from './videoUi';
import { AccountAvatar, PlatformChip, PostRow, TZ_LABEL, YoutubeConsent, toLocalInput } from './socialUi';
import '../landing/landing.css';

const PROVIDER_HINT: Record<SocialProvider, string> = {
    youtube: 'Channel YouTube (video & Shorts)',
    tiktok: 'Akun TikTok',
    linkedin: 'Posting sebagai profil LinkedIn pribadimu',
    linkedin_pages: 'Company Page tempat kamu menjadi admin',
    meta: 'Facebook Page + Instagram Business/Creator yang terhubung ke Page (Reel & Story)',
    threads: 'Profil Threads (video post)',
};

/** /video/schedule — connected accounts + every scheduled / sent post. */
export const ScheduleView: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const [cfg, setCfg] = useState<SocialConfig | null>(null);
    const [accounts, setAccounts] = useState<SocialAccount[] | null>(null);
    const [posts, setPosts] = useState<ScheduledPost[] | null>(null);
    const [moving, setMoving] = useState<ScheduledPost | null>(null);
    const [statsBusy, setStatsBusy] = useState(false);

    const load = useCallback(() => {
        socialService.accounts().then(setAccounts).catch(() => setAccounts([]));
        socialService.posts().then(setPosts).catch(() => setPosts([]));
    }, []);
    useEffect(() => {
        socialService.config().then(setCfg).catch(() => setCfg(null));
        load();
    }, [load]);

    // Engagement is read from the platforms; the server skips numbers under 10 minutes old.
    const refreshStats = useCallback((quiet: boolean) => {
        setStatsBusy(true);
        socialService
            .refreshInsights()
            .then(() => socialService.posts().then(setPosts))
            .catch((e) => !quiet && toast.error(errorText(e)))
            .finally(() => setStatsBusy(false));
    }, [toast]);
    useEffect(() => {
        refreshStats(true);
    }, [refreshStats]);

    // Refresh while something is going out.
    const busy = posts?.some((p) => p.status === 'PUBLISHING' || (p.status === 'SCHEDULED' && new Date(p.scheduledAt).getTime() < Date.now() + 60_000));
    useEffect(() => {
        if (!busy) return;
        const t = window.setInterval(() => socialService.posts().then(setPosts).catch(() => {}), 5000);
        return () => window.clearInterval(t);
    }, [busy]);

    const upcoming = useMemo(() => (posts || []).filter((p) => p.status === 'SCHEDULED' || p.status === 'PUBLISHING').sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt)), [posts]);
    const history = useMemo(() => (posts || []).filter((p) => p.status !== 'SCHEDULED' && p.status !== 'PUBLISHING'), [posts]);
    const byDay = useMemo(() => {
        const m = new Map<string, ScheduledPost[]>();
        for (const p of upcoming) {
            const k = new Date(p.scheduledAt).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
            m.set(k, [...(m.get(k) || []), p]);
        }
        return Array.from(m.entries());
    }, [upcoming]);

    const connect = async (p: SocialProvider) => {
        try {
            await socialService.connect(p);
        } catch (e) {
            toast.error(errorText(e));
        }
    };
    const disconnect = async (a: SocialAccount) => {
        const pending = upcoming.filter((p) => p.account.id === a.id).length;
        if (!window.confirm(`Putuskan ${a.name}?${pending ? ` ${pending} jadwal ke akun ini ikut dibatalkan.` : ''} Post yang sudah tayang tetap ada di platform.`)) return;
        try {
            await socialService.disconnect(a.id);
            load();
        } catch (e) {
            toast.error(errorText(e));
        }
    };
    const act = async (fn: () => Promise<unknown>) => {
        try {
            await fn();
            load();
        } catch (e) {
            toast.error(errorText(e));
        }
    };
    const row = (p: ScheduledPost) => (
        <PostRow key={p.id} post={p} showVideo onCancel={() => act(() => socialService.cancel(p.id))} onRetry={() => act(() => socialService.retry(p.id))} onReschedule={() => setMoving(p)} />
    );

    return (
        <div className="hv-root" data-hv-theme={readTheme()} style={{ minHeight: '100vh', background: 'var(--hv-desk)', fontFamily: FONT_SANS, color: 'var(--hv-ink)', padding: '28px 20px 60px' }}>
            <SEOHead title="Jadwal posting" noindex={true} />
            <div style={{ maxWidth: 1120, margin: '0 auto' }}>
                <button type="button" onClick={() => navigate('/video')} style={{ border: 0, background: 'transparent', color: 'var(--hv-muted)', cursor: 'pointer', fontSize: 13.5, padding: 0 }}>← Video maker</button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18 }}>
                    <span style={{ fontFamily: FONT_MONO, fontSize: 11.5, letterSpacing: '0.2em', color: 'var(--hv-leaf)' }}>JADWAL POSTING</span>
                    <BetaChip />
                </div>
                <h1 style={{ margin: '10px 0 0', fontFamily: FONT_SERIF, fontWeight: 400, fontSize: 44, lineHeight: 1.05, letterSpacing: '-0.02em' }}>Sekali render, <i>tayang di mana-mana.</i></h1>
                {cfg && !cfg.enabled && <p style={{ color: 'var(--hv-danger)' }}>Penjadwalan belum tersedia di server ini.</p>}

                <div style={{ marginTop: 30, display: 'flex', flexWrap: 'wrap', gap: 28, alignItems: 'flex-start' }}>
                    <div style={{ flex: '1 1 280px', maxWidth: 380, minWidth: 0 }}>
                        <div style={fieldLabel}>Akun terhubung</div>
                        <div style={{ display: 'grid', gap: 8 }}>
                            {accounts === null && <Spinner />}
                            {accounts?.map((a) => (
                                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 14, background: 'var(--hv-card)', border: '1px solid var(--hv-line)' }}>
                                    <AccountAvatar account={a} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 13.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.name}</div>
                                        <PlatformChip platform={a.platform} />
                                        {a.status !== 'ACTIVE' && <div style={{ fontSize: 12, color: 'var(--hv-danger)' }}>Koneksi kedaluwarsa</div>}
                                    </div>
                                    <button type="button" title="Putuskan" data-track="social_disconnect" data-track-label={a.platform} onClick={() => disconnect(a)} style={{ border: 0, background: 'transparent', color: 'var(--hv-muted)', cursor: 'pointer', fontSize: 12.5, fontFamily: 'inherit' }}>Putuskan</button>
                                </div>
                            ))}
                        </div>
                        <div style={{ ...fieldLabel, marginTop: 22 }}>Hubungkan</div>
                        <div style={{ display: 'grid', gap: 8 }}>
                            {(Object.keys(PROVIDER_LABEL) as SocialProvider[]).map((p) => {
                                const on = !!cfg?.providers[p];
                                return (
                                    <React.Fragment key={p}>
                                    <button type="button" disabled={!on} data-track="social_connect" data-track-label={p} onClick={() => connect(p)} className={on ? 'hv-h-lift' : undefined} style={{ textAlign: 'left', padding: '11px 14px', borderRadius: 14, border: '1px dashed var(--hv-line-2)', background: 'transparent', color: on ? 'var(--hv-ink)' : 'var(--hv-muted)', cursor: on ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>
                                        <div style={{ fontSize: 13.5, fontWeight: 700 }}>+ {PROVIDER_LABEL[p]}</div>
                                        <div style={{ fontSize: 12, color: 'var(--hv-muted)', marginTop: 2 }}>{on ? PROVIDER_HINT[p] : 'Belum dikonfigurasi di server'}</div>
                                    </button>
                                    {p === 'youtube' && on && <div style={{ padding: '0 4px 4px' }}><YoutubeConsent connecting /></div>}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                        <p style={{ marginTop: 14, fontSize: 12, color: 'var(--hv-muted)', lineHeight: 1.55 }}>
                            HyVay hanya meminta izin untuk mengunggah video atas namamu. Token disimpan terenkripsi, dan kamu bisa memutuskannya kapan saja.
                        </p>
                    </div>

                    <div style={{ flex: '999 1 340px', minWidth: 0 }}>
                        <div style={fieldLabel}>Akan datang</div>
                        {posts === null && <Spinner />}
                        {posts && !upcoming.length && (
                            <div style={{ padding: 28, borderRadius: 18, border: '1px dashed var(--hv-line-2)', color: 'var(--hv-muted)', fontSize: 13.5, textAlign: 'center' }}>
                                Belum ada jadwal. Buka video, render, lalu klik <b>Posting / Jadwalkan</b>.
                            </div>
                        )}
                        <div style={{ display: 'grid', gap: 18 }}>
                            {byDay.map(([day, ps]) => (
                                <div key={day}>
                                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{day}</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 8 }}>{ps.map(row)}</div>
                                </div>
                            ))}
                        </div>
                        {history.length > 0 && (
                            <>
                                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginTop: 30 }}>
                                    <div style={fieldLabel}>Riwayat</div>
                                    <button type="button" disabled={statsBusy} onClick={() => refreshStats(false)} style={{ border: 0, background: 'transparent', padding: 0, cursor: statsBusy ? 'default' : 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, color: statsBusy ? 'var(--hv-muted)' : 'var(--hv-ink)' }}>
                                        {statsBusy ? 'Memperbarui statistik…' : 'Perbarui statistik'}
                                    </button>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 8 }}>{history.map(row)}</div>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <RescheduleDialog post={moving} maxAheadDays={cfg?.maxAheadDays ?? 90} onClose={() => setMoving(null)} onSaved={load} />
        </div>
    );
};

/** Move a scheduled post to another time. */
export const RescheduleDialog: React.FC<{ post: ScheduledPost | null; maxAheadDays: number; onClose: () => void; onSaved: () => void }> = ({ post, maxAheadDays, onClose, onSaved }) => {
    const toast = useToast();
    const [at, setAt] = useState('');
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (post) setAt(toLocalInput(new Date(post.scheduledAt)));
    }, [post]);
    const save = async () => {
        if (!post) return;
        setBusy(true);
        try {
            await socialService.update(post.id, { scheduledAt: new Date(at).toISOString() });
            onSaved();
            onClose();
        } catch (e) {
            toast.error(errorText(e));
        } finally {
            setBusy(false);
        }
    };
    const valid = !!at && new Date(at).getTime() > Date.now();
    return (
        <Modal open={!!post} onClose={onClose} maxWidth={420} label="Ubah waktu posting">
            <div style={{ padding: 22, display: 'grid', gap: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ flex: 1, fontFamily: FONT_SERIF, fontSize: 24 }}>Ubah waktu</div>
                    <CloseX onClick={onClose} />
                </div>
                <input type="datetime-local" value={at} min={toLocalInput(new Date())} max={toLocalInput(new Date(Date.now() + maxAheadDays * 86400_000))} onChange={(e) => setAt(e.target.value)} style={input} />
                {TZ_LABEL && <div style={{ fontSize: 12, color: 'var(--hv-muted)' }}>Zona waktu: {TZ_LABEL}</div>}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <GhostBtn onClick={onClose}>Batal</GhostBtn>
                    <BrandBtn onClick={save} disabled={!valid || busy}>{busy ? <Spinner light /> : 'Simpan'}</BrandBtn>
                </div>
            </div>
        </Modal>
    );
};
